import type { DialogConf } from '@Primitive/ExDialog';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { ToolbarIconInfo } from '@Primitive/ToolbarIconButton';
import { SettingsElementAll } from '@Primitive/SettingsListType';
// --------------------------------------------------------------------------------
export const enum PortKeys {
    Data = 'data',
    SaveForm = 'saveform',
    SaveResults = 'saveresults',
    Exit = 'exit',
    DeleteForm = 'deleteform',
    ToolbarButtonClicked = 'toolbarbuttonclicked',
    FormButtonClicked = 'formbuttonclicked'
}
// --------------------------------------------------------------------------------
export interface QuestionnaireData {
    editable: boolean;
    toolbarButtons: ToolbarIconInfo[];
}
// --------------------------------------------------------------------------------
export interface QuestionnaireState {
    dialogInfo: DialogConf;
    questions: IQuestionnaireQuestion[]; // from input port
    searchValue: string;
    showPreview: boolean; // when editable
    showQuestionDetails: boolean; // when editable
    results: IQuestionnaireResult[]; // question answers
    photos: IQuestionnairePhoto[];
    exitRules: IQuestionnaireExitRule[]; // finish condition
    exitRuleFulfilled: boolean;
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireDataPort {
    questions: IQuestionnaireQuestion[];
    categories: IQuestionnaireOption[];
    categoryId: number; // of sku
    families: IQuestionnaireOption[];
    familyId: number; // of sku
    results: IQuestionnaireResult[]; // answered
    photos: IQuestionnairePhoto[];
    exitRules: IQuestionnaireExitRule[];
    readonly: boolean; // review
    bottomElements: SettingsElementAll<IQuestionnaireAnswerableFormData>[]; // when answerable
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireButton {
    id: string; // data path
    label: string;
    icon: string;
    backgroundColor: string;
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireExitRule {
    message: string; // markdown
    rules: IQuestionnaireRule[];
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireOption {
    id: number;
    name: string;
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireQuestion {
    id: string; // guid
    type: IQuestionnaireQuestionType;
    group: number;
    order: number;
    label: string;
    tooltip: string;
    rules: IQuestionnaireRule[]; // all must be verified to show the question
    weights: IQuestionnaireWeight[];
    options: IQuestionnaireQuestionOption[]; // when type is select
    multiple: boolean; // when type is select
    help: IQuestionnaireHelp;
    mandatory: boolean; // *
    report: IQuestionnaireReport; // k-retail
    imageSrc: string; // when type is photo marker
    dataLake: IQuestionnaireDataLake;
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireReport {
    priority: number;
    label: string;
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireDataLake {
    headerName: string; // csv column -> q[1,30]
}
// --------------------------------------------------------------------------------
// markdown
export interface IQuestionnaireHelp {
    textValue: string; // show always
    dialogValue: string; // button trigger
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireQuestionOption {
    label: string;
    value: string;
}
// --------------------------------------------------------------------------------
export enum IQuestionnaireQuestionType {
    Boolean,
    Select,
    Text,
    QrCode,
    Photo,
    PhotoMarker
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireRule {
    type: IQuestionRuleType;
    questionId: string; // question type
    value: boolean | string; // visible when question has this value
}
// --------------------------------------------------------------------------------
export enum IQuestionRuleType {
    Question,
    ProductFamily,
    ProductCategory
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireWeight {
    type: IQuestionWeightType;
    value: boolean | string; // question's result
    commonWeight: number; // common type
    classWeights: IQuestionnaireClassWeight[]; // for family or category type
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireClassWeight {
    classId: number; // family or category id
    weight: number;
}
// --------------------------------------------------------------------------------
export enum IQuestionWeightType {
    Common,
    ProductFamily,
    ProductCategory
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireSaveFormPort {
    questions: IQuestionnaireQuestion[];
    exitRules: IQuestionnaireExitRule[];
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireSaveResultsPort {
    results: IQuestionnaireResult[];
    photos: IQuestionnairePhoto[];
    score: number;
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireResult {
    questionId: string;
    value: boolean | string;
}
// --------------------------------------------------------------------------------
export interface IQuestionnairePhoto {
    questionId: string; // auto filled
    src: string;
    description: string;
    position: IQuestionnairePhotoPosition;
}
// --------------------------------------------------------------------------------
export enum IQuestionnairePhotoPosition {
    Front,
    Back,
    Side
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireEditableFormData {
    questions: IQuestionnaireQuestion[];
    exitRules: IQuestionnaireExitRule[];
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireAnswerableFormData {
    results: IQuestionnaireResult[];
    photos: IQuestionnairePhoto[];
}
// --------------------------------------------------------------------------------
export interface IQuestionnaireFormButtonClickedPort {
    element: SettingsElementAll<IQuestionnaireAnswerableFormData>;
    data: IQuestionnaireAnswerableFormData;
}
// --------------------------------------------------------------------------------
export const QuestionnaireDataPortValidator = () => {
    return JsonSchemaValidator.GetInstance('questionnaire_data_port_validator', {
        type: 'object',
        properties: {}
    });
};
// --------------------------------------------------------------------------------